import React, {
  useEffect,
  useRef,
  useState,
  useContext,
  useCallback,
} from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";

import { AuthContext } from "../contexts/AuthContext";

import ContactList from "./ContactList";
import MessageBubble from "./MessageBubble";
import api from "../api/api";

import "./ChatBox.css";

export default function ChatBox() {
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [naoLidasPorContato, setNaoLidasPorContato] = useState({});
  const { user } = useContext(AuthContext);
  const [currentUser, setCurrentUser] = useState(null);
  const [search, setSearch] = useState('-');
  const chatEndRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const userRes = await api.get(`perfil/email/${user.email}`);
        setCurrentUser(userRes.data);

        const contatosRes = await api.get(`/perfil/contatosChat/${user.email}/${search === '' ? '-' : search}`);
        setContacts(contatosRes.data);
      } catch (error) {
        console.error("Erro ao carregar dados iniciais:", error);
      }
    };

    fetchInitialData();
  }, [user.email, search]);

  useEffect(() => {
    if (currentUser?.email) {
      getMensagemNaoLida();
    }
  }, [currentUser]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const getMensagemNaoLida = useCallback(async () => {
    try {
      const response = await api.get(`mensagens/mensagemNaoLida/${user.email}`);

      const agrupado = response.data.reduce((acc, msg) => {
        const email = msg.emissor?.email?.trim();
        if (email) {
          acc[email] = (acc[email] || 0) + 1;
        }
        return acc;
      }, {});

      setNaoLidasPorContato(agrupado);
    } catch (error) {
      console.error("Erro ao obter mensagens não lidas:", error);
    }
  }, [user.email]);

  const fetchConversa = useCallback(async () => {
    if (!selectedContact || !currentUser?.email) return;

    try {
      const response = await api.get("mensagens/conversaEntre", {
        params: {
          email1: currentUser.email,
          email2: selectedContact.email,
        },
      });

      setMessages(response.data);
      getMensagemNaoLida();
    } catch (error) {
      console.error("Erro ao carregar conversa:", error);
      setMessages([]);
    }
  }, [selectedContact, currentUser, getMensagemNaoLida]);

  useEffect(() => {
    if (selectedContact && currentUser?.email) {
      fetchConversa();

      pollingIntervalRef.current = setInterval(() => {
        fetchConversa();
      }, 10000);

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    }
  }, [selectedContact, currentUser, fetchConversa]);

  useEffect(() => {
    if (currentUser?.email) {
      const naoLidasInterval = setInterval(() => {
        getMensagemNaoLida();
      }, 15000);

      return () => clearInterval(naoLidasInterval);
    }
  }, [currentUser, getMensagemNaoLida]);

  const formik = useFormik({
    initialValues: {
      mensagem: "",
    },
    validationSchema: Yup.object({
      mensagem: Yup.string().required("Digite sua mensagem"),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (!selectedContact) return;

      const payload = {
        emissorId: currentUser.id,
        receptorId: selectedContact.id,
        emissorEmail: currentUser.email,
        emissorNome: currentUser.nome,
        mensagem: values.mensagem,
        mensagemLida: false,
        mensagemRespondida: false,
        mensagemAnterior: null,
      };

      try {
        const response = await api.post("mensagens", payload, {
          headers: {
            email: currentUser.email,
          },
        });

        setMessages((prev) => [...prev, response.data]);
        resetForm();
        getMensagemNaoLida();
      } catch (error) {
        console.error("Erro ao enviar mensagem:", error);
      }
    },
  });

  return (
    <div className="chat-layout">
      <ContactList
        contacts={contacts}
        onSelect={setSelectedContact}
        selectedEmail={selectedContact?.email}
        naoLidas={naoLidasPorContato}
        search={search}
        setSearch={setSearch}
      />

      <div className="chat-container">
        <div className="chat-header">
          <h3>
            Conversando com {selectedContact ? selectedContact.nome : "..."}
          </h3>
        </div>

        <div className="chat-body">
          {messages.length === 0 && selectedContact && (
            <p>Nenhuma mensagem ainda. Comece a conversar!</p>
          )}

          {messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg.mensagem}
              isSender={msg.emissorEmail === currentUser.email}
              createdAt={msg.createdAt}
              mensagemLida={msg.mensagemLida}
            />
          ))}
          <div ref={chatEndRef} />
        </div>

        {selectedContact && (
          <form onSubmit={formik.handleSubmit} className="chat-footer">
            <InputTextarea
              id="mensagem"
              name="mensagem"
              autoResize
              rows={1}
              placeholder="Digite sua mensagem..."
              value={formik.values.mensagem}
              onChange={formik.handleChange}
              className="chat-input"
            />
            <Button
              icon="pi pi-send"
              type="submit"
              className="p-button-rounded p-button-text"
              disabled={!formik.values.mensagem}
            />
          </form>
        )}
      </div>
    </div>
  );
}